import { useEffect, useState, useCallback, useMemo } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
    CheckSquare, CheckCircle, XCircle, CircleNotch, CaretDown, CaretUp,
    Calendar, Stack, ArrowsClockwise,
} from "@phosphor-icons/react";

function fmtDate(d) {
    if (!d) return "—";
    const [y, m, dd] = d.split("-");
    return `${dd}/${m}/${y}`;
}

export default function TimesheetApprovals() {
    const { user } = useAuth();
    const isManage = ["admin", "manager"].includes(user?.role);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await endpoints.timeEntries({ approval_status: "pending", per_page: 300 });
            setEntries(data?.items || []);
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const groups = useMemo(() => {
        const byBatch = new Map();
        const loose = [];
        for (const e of entries) {
            const bid = e.week_batch_id && String(e.week_batch_id).trim();
            if (bid) {
                if (!byBatch.has(bid)) byBatch.set(bid, { type: "batch", batch_id: bid, entries: [] });
                byBatch.get(bid).entries.push(e);
            } else loose.push({ type: "single", entry: e });
        }
        const batches = [];
        for (const b of byBatch.values()) {
            if (b.entries.length >= 2) batches.push(b);
            else loose.push({ type: "single", entry: b.entries[0] });
        }
        return { batches, loose };
    }, [entries]);

    async function act(fn, okMsg) {
        setBusy(true);
        try { await fn(); await load(); }
        catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }

    const approve = (id) => act(() => endpoints.approveTimeEntry(id));
    const reject = (id, note) => act(() => endpoints.rejectTimeEntry(id, { note }));
    const approveBatch = (bid) => act(() => endpoints.bulkApproveTime({ week_batch_id: bid }));
    const rejectBatch = (bid, note) => act(() => endpoints.bulkRejectTime({ week_batch_id: bid, note }));
    const approveAll = () => act(() => endpoints.bulkApproveTime({ entry_ids: entries.map((e) => e.id) }));

    if (!isManage) {
        return <div className="max-w-2xl mx-auto px-6 py-16 text-center text-[var(--rom-text-2)]" data-testid="approvals-denied">Approvals are available to managers and admins only.</div>;
    }

    const total = entries.length;

    return (
        <div className="max-w-[1200px] mx-auto px-6 py-8 space-y-5" data-testid="approvals-page">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">Timesheets</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <CheckSquare size={28} weight="bold" className="text-[var(--rom-orange)]" /> Approvals
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1">{total} submission{total !== 1 ? "s" : ""} awaiting review.</p>
                </div>
                <button onClick={load} className="btn btn-ghost"><ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
            </header>

            {total > 1 && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-[6px] bg-[var(--rom-green)]/10 border border-[var(--rom-green)]/30">
                    <CheckCircle size={14} className="text-[var(--rom-green)]" weight="bold" />
                    <p className="text-xs flex-1">{total} entries pending. Review individually, or —</p>
                    <button data-testid="approve-all-btn" onClick={approveAll} disabled={busy} className="btn btn-sm" style={{ background: "var(--rom-green)", color: "white" }}>Approve all ({total})</button>
                </div>
            )}

            {loading ? (
                <div className="rom-card"><div className="rom-card-body text-center py-10 text-[var(--rom-text-3)]">Loading…</div></div>
            ) : total === 0 ? (
                <div className="rom-card"><div className="rom-card-body text-center py-12">
                    <CheckCircle size={32} className="mx-auto mb-3 text-[var(--rom-green)]" weight="fill" />
                    <p className="font-semibold">All caught up!</p>
                    <p className="text-xs text-[var(--rom-text-3)] mt-1">No pending submissions.</p>
                </div></div>
            ) : (
                <div className="space-y-3">
                    {groups.batches.map((b) => (
                        <WeekBatchCard key={b.batch_id} batch={b} busy={busy}
                            onApproveBatch={approveBatch} onRejectBatch={rejectBatch} onApprove={approve} onReject={reject} />
                    ))}
                    {groups.loose.length > 0 && (
                        <div className="rom-card overflow-hidden">
                            <div className="rom-card-head"><h3>Individual entries</h3></div>
                            <div className="overflow-x-auto">
                                <table className="rom-table">
                                    <thead><tr><th>Date</th><th>Type</th><th>Employee</th><th>Description</th><th>Project</th><th className="numeric">Hours</th><th>Actions</th></tr></thead>
                                    <tbody>
                                        {groups.loose.map((g) => <ApprovalRow key={g.entry.id} entry={g.entry} busy={busy} onApprove={approve} onReject={reject} />)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function ApprovalRow({ entry, onApprove, onReject, busy, compact }) {
    const [showReject, setShowReject] = useState(false);
    const [note, setNote] = useState("");
    return (
        <tr data-testid={`approval-row-${entry.id}`}>
            <td className="font-mono text-xs whitespace-nowrap">{fmtDate(entry.work_date)}</td>
            <td><span className={`chip ${entry.entry_type === "time_off" ? "chip-orange" : "chip-navy"}`}>{entry.entry_type === "time_off" ? "Time off" : "Work"}</span></td>
            {!compact && <td className="font-semibold">{entry.employee_name}</td>}
            <td className="text-[var(--rom-text-2)] max-w-[180px] truncate">{entry.type_raw || entry.time_off_type || "—"}</td>
            <td className="text-[var(--rom-text-2)] text-xs font-mono max-w-[160px] truncate" data-testid={`approval-project-${entry.id}`}>{entry.project_name || entry.job_ref || "General"}</td>
            <td className="numeric font-semibold">{(entry.decimal_hours || 0).toFixed(1)}h{entry.overtime_hours > 0 && <span className="text-[var(--rom-orange-dark)] ml-1">+{entry.overtime_hours.toFixed(1)} OT</span>}</td>
            <td>
                <div className="flex flex-col gap-1.5 items-start">
                    <div className="flex gap-1.5">
                        <button data-testid={`approve-btn-${entry.id}`} onClick={() => onApprove(entry.id)} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 rounded-[4px] text-white text-xs font-semibold disabled:opacity-50" style={{ background: "var(--rom-green)" }}>
                            <CheckCircle size={11} weight="bold" /> Approve
                        </button>
                        <button data-testid={`reject-toggle-btn-${entry.id}`} onClick={() => setShowReject((s) => !s)} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 rounded-[4px] border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold disabled:opacity-50">
                            <XCircle size={11} weight="bold" /> Reject {showReject ? <CaretUp size={10} /> : <CaretDown size={10} />}
                        </button>
                    </div>
                    {showReject && (
                        <div className="flex gap-1.5 items-center w-full">
                            <input data-testid={`reject-note-${entry.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (required)" autoFocus
                                className="flex-1 h-7 px-2 rounded-[4px] border border-red-200 text-xs focus:outline-none" />
                            <button data-testid={`reject-confirm-btn-${entry.id}`} onClick={() => { if (note.trim()) { onReject(entry.id, note); setShowReject(false); setNote(""); } }} disabled={!note.trim()} className="px-2 py-1 rounded-[4px] bg-red-600 text-white text-xs font-semibold disabled:opacity-50">Confirm</button>
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
}

function WeekBatchCard({ batch, onApproveBatch, onRejectBatch, onApprove, onReject, busy }) {
    const [expanded, setExpanded] = useState(false);
    const [showReject, setShowReject] = useState(false);
    const [note, setNote] = useState("");
    const sorted = [...batch.entries].sort((a, b) => (a.work_date || "").localeCompare(b.work_date || ""));
    const totalHours = batch.entries.reduce((s, e) => s + (e.decimal_hours || 0), 0);
    const name = batch.entries[0]?.employee_name || "Unknown";

    return (
        <div className="rom-card overflow-hidden" data-testid={`week-batch-${batch.batch_id}`} style={{ borderLeft: "3px solid var(--rom-navy)" }}>
            <div className="flex items-center justify-between gap-4 px-4 py-3 bg-slate-50 border-b border-[var(--rom-border)]">
                <button onClick={() => setExpanded((e) => !e)} className="flex items-center gap-3 text-left flex-1 min-w-0" data-testid={`week-batch-toggle-${batch.batch_id}`}>
                    <Stack size={16} className="text-[var(--rom-navy)] shrink-0" weight="bold" />
                    <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">Weekly submission — {name}</p>
                        <p className="text-xs text-[var(--rom-text-2)] flex items-center gap-1.5 flex-wrap">
                            <Calendar size={11} /> {fmtDate(sorted[0]?.work_date)} → {fmtDate(sorted[sorted.length - 1]?.work_date)} · <span className="font-semibold">{totalHours.toFixed(1)}h</span> · {batch.entries.length} days
                        </p>
                    </div>
                    {expanded ? <CaretUp size={14} className="text-[var(--rom-text-3)]" /> : <CaretDown size={14} className="text-[var(--rom-text-3)]" />}
                </button>
                <div className="flex items-center gap-1.5 shrink-0">
                    <button data-testid={`approve-week-btn-${batch.batch_id}`} onClick={() => onApproveBatch(batch.batch_id)} disabled={busy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] text-white text-xs font-semibold disabled:opacity-50" style={{ background: "var(--rom-green)" }}>
                        {busy ? <CircleNotch size={11} className="animate-spin" /> : <CheckCircle size={11} weight="bold" />} Approve week
                    </button>
                    <button data-testid={`reject-week-btn-${batch.batch_id}`} onClick={() => setShowReject((s) => !s)} disabled={busy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold disabled:opacity-50">
                        <XCircle size={11} weight="bold" /> Reject week
                    </button>
                </div>
            </div>
            {showReject && (
                <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border-b border-red-100">
                    <input data-testid={`reject-week-note-${batch.batch_id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for rejecting the whole week (required)" autoFocus
                        className="flex-1 h-8 px-3 rounded-[4px] border border-red-200 text-xs focus:outline-none bg-white" />
                    <button data-testid={`reject-week-confirm-${batch.batch_id}`} onClick={() => { if (note.trim()) { onRejectBatch(batch.batch_id, note); setShowReject(false); setNote(""); } }} disabled={!note.trim()} className="px-3 py-1.5 rounded-[4px] bg-red-600 text-white text-xs font-semibold disabled:opacity-50">Reject week</button>
                </div>
            )}
            {expanded && (
                <div className="overflow-x-auto">
                    <table className="rom-table">
                        <thead><tr><th>Date</th><th>Type</th><th>Description</th><th>Project</th><th className="numeric">Hours</th><th>Actions</th></tr></thead>
                        <tbody>{sorted.map((e) => <ApprovalRow key={e.id} entry={e} busy={busy} onApprove={onApprove} onReject={onReject} compact />)}</tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
